import { useMemo } from "react";
import type {
  Booking,
  CountryConfig,
  CustomerPreOrder,
  PreorderMenu,
  PreorderMenuItemOrHeading,
  VenueConfig,
} from "../../../common/jsonRpcTypes";
import type { PendingBooking, PreorderSelections } from "../bookingForm/BookingForm";
import type { LinkedBooking } from "../context/PreorderContext";
import { resolveCurrencyCode } from "../../../utils/countryLocaleMap";

interface UseBookPreorderAdapterParams {
  /** The pending booking created during the /book flow. */
  pendingBooking: PendingBooking | null;
  /** Preorder menus for the selected booking type. */
  preorderMenus: PreorderMenu[];
  /** Linked booking types with their preorder menus. */
  linkedBookings: LinkedBooking[] | null;
  /** Venue configuration from the create-pending-booking response. */
  venueConfig: VenueConfig | null;
  /** Country configuration (currency, country code). */
  countryConfig: CountryConfig | null;
  /** Service charge percentage from the selected booking type. */
  serviceChargePercentage: number;
  /**
   * Whether the venue hides the allergens and dietary controls, as configured
   * in Collins. Defaults to false so the controls remain visible when the
   * setting is unknown.
   */
  hideDietaryRequirements?: boolean;
  /**
   * Previously confirmed pre-order selections, supplied when the guest returns
   * to the pre-order step (e.g. pressing "Back" from payment). When present,
   * the synthesised CustomerPreOrder is pre-populated with these items, totals
   * and service charge so the basket is restored without an extra API call.
   */
  restoredSelections?: PreorderSelections | null;
}

interface UseBookPreorderAdapterReturn {
  /** Synthesised Booking object compatible with PreorderExperience. */
  bookingData: Booking | undefined;
  /** Booking ID (the pending booking ID). */
  bookingId: string;
  /** Synthesised CustomerPreOrder from preorder menus. */
  initialCustomerPreOrder: CustomerPreOrder | undefined;
  /** Default service charge percentage. */
  defaultServiceChargePercentage: number;
  /** Whether all required data is available. */
  ready: boolean;
  /** Linked booking types with their preorder menus. */
  linkedBookings: LinkedBooking[] | null;
  /** Booking type name for the main booking. */
  bookingTypeName?: string;
  /** Booking type ID for the main booking. */
  bookingTypeId?: string;
}

/**
 * Resolve the currency the guest is charged in. The venue's own currency is
 * authoritative — a venue may trade in a currency that differs from its
 * country's default (e.g. a GB venue charging in USD) — so the country
 * configuration is only a fallback. The country code remains the locale hint
 * for number formatting, never the source of the currency.
 */
const resolveCurrency = (venueConfig: VenueConfig | null, countryConfig: CountryConfig | null): string =>
  resolveCurrencyCode(venueConfig?.currency, countryConfig?.currency);

/**
 * useBookPreorderAdapter — transforms /book route state into shapes
 * compatible with PreorderExperience.
 *
 * The /book flow has a PendingBooking + PreorderMenu[] + VenueConfig,
 * whereas PreorderExperience expects a Booking + CustomerPreOrder.
 * This hook synthesises the required shapes without making additional
 * API calls.
 */
export function useBookPreorderAdapter({
  pendingBooking,
  preorderMenus,
  linkedBookings,
  venueConfig,
  countryConfig,
  serviceChargePercentage,
  hideDietaryRequirements = false,
  restoredSelections,
}: UseBookPreorderAdapterParams): UseBookPreorderAdapterReturn {
  const bookingId = pendingBooking?._id ?? "";

  const bookingData: Booking | undefined = useMemo(() => {
    if (!pendingBooking || !venueConfig) return undefined;

    return {
      bookingId: pendingBooking._id,
      bookingRef: "",
      depositTotal: null,
      guestCount: pendingBooking.num_people,
      time: pendingBooking.time,
      date: pendingBooking.date,
      venue: {
        title: venueConfig.title,
        currency: resolveCurrency(venueConfig, countryConfig),
        country_code: countryConfig?.country_code ?? null,
        photo: undefined,
        hideDietaryRequirements,
      },
      paymentProcessor: "",
      depositTypePayment: false,
    };
  }, [pendingBooking, venueConfig, countryConfig, hideDietaryRequirements]);

  const initialCustomerPreOrder: CustomerPreOrder | undefined = useMemo(() => {
    if (!pendingBooking || !venueConfig || preorderMenus.length === 0) return undefined;

    const currency = resolveCurrency(venueConfig, countryConfig);
    const countryCode = countryConfig?.country_code ?? "GB";

    const menus = preorderMenus.map((menu) => ({
      id: menu.id,
      name: menu.name,
      description: menu.description,
      fixed_price: !!menu.fixed_price_rules?.length,
      fixed_price_rules: menu.fixed_price_rules,
      min_items: menu.limit_preorders?.min,
      max_items: menu.limit_preorders?.max,
      // Preserve minimum-spend rules so the shared Menu validation (minimum spend
      // per guest / per booking) works in the /book flow as it does elsewhere.
      minimum_preorder_amount: menu.minimum_preorder_amount,
      minimum_preorder_amount_per: menu.minimum_preorder_amount_per,
      items: menu.items.map((item: PreorderMenuItemOrHeading) => {
        if ("type" in item && item.type === "heading") {
          return item;
        }
        if (!("id" in item)) {
          return { type: "food" as const, id: "", name: "", price: 0 };
        }
        return {
          id: item.id,
          name: item.name,
          price: item.price,
          description: item.description,
          type: "food" as const,
          sub_type: item.sub_type,
          quantity: 0,
          configurable_options: item.configurable_options,
          // Carried through so the shared menu can mark items unsuitable for a
          // guest's allergies and diet types, matching the /place-your-order
          // behaviour. Without them the menu also drops the "Dietary:" labels.
          allergens: item.allergens,
          diet_types: item.diet_types,
          // Linked-booking menus skip the server-processed menu data, so these
          // raw items are the only source of the calorie note shown with the
          // item description.
          calories: item.calories,
        };
      }),
    }));

    // When the guest returns to the pre-order step (e.g. from payment), restore
    // their previously confirmed items, totals and service charge so the basket
    // is populated immediately rather than appearing empty until another request.
    const restored = restoredSelections && restoredSelections.rawItems.length > 0 ? restoredSelections : null;

    return {
      totalCost: restored ? restored.subtotal : 0,
      totalCostWithService: restored ? restored.total : 0,
      serviceChargePercentage: restored ? restored.serviceChargePercentage : serviceChargePercentage,
      serviceChargeAmount: restored ? restored.serviceChargeAmount : 0,
      // Restored alongside the totals: the service charge selector is only offered
      // when the pre-order is being paid for now, so losing this on the way back
      // from payment would withdraw the guest's control over a charge they pay.
      requiredPaymentAmount: restored?.requiredPaymentAmount,
      requiredPaymentGross: restored?.requiredPaymentGross,
      pendingPreorderId: restored?.pendingPreorderId,
      // The booking created during the /book flow is not yet finalised with
      // Collins API, so every RPC call needs `pending: true` to resolve the
      // customer preorder from the pending-booking cache rather than the
      // (not-yet-existing) real customer-preorder record.
      pending: true,
      booking: {
        _id: pendingBooking._id,
        date: pendingBooking.date,
        venue_id: pendingBooking.venue_id,
        venue: {
          _id: pendingBooking.venue_id,
          title: venueConfig.title,
          photo: false,
          country_code: countryCode,
          currency,
          preorder_items: [],
        },
        num_people: pendingBooking.num_people,
      },
      menus,
      items: restored ? restored.rawItems : [],
      // Restored alongside the items: the dietary modal's selections (and the
      // shared order note they carry) were saved on the previous pass through
      // this step, so stepping back must repopulate them for the modal, the
      // summary and the customer_notes derivation at the next completion.
      // Taken from the selections rather than `restored`, as the modal also
      // filters the menu before any item is added; they are only submitted
      // with a pre-order that has items.
      dietaryRequirements: restoredSelections?.dietaryRequirements,
    };
  }, [pendingBooking, venueConfig, countryConfig, preorderMenus, serviceChargePercentage, restoredSelections]);

  const ready = !!(bookingId && bookingData && initialCustomerPreOrder);
  const bookingTypeName = pendingBooking?.type?.name;
  const bookingTypeId = pendingBooking?.type?.id;

  return {
    bookingData,
    bookingId,
    initialCustomerPreOrder,
    defaultServiceChargePercentage: serviceChargePercentage,
    ready,
    linkedBookings,
    bookingTypeName,
    bookingTypeId,
  };
}
